import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Search, Edit3, Rocket, X, CheckCircle, Crown, Zap, Brain, DollarSign } from "lucide-react";
import { toast } from "sonner";
import TestimonialsSection from "../components/TestimonialsSection";
import CTAFooter from "../components/CTAFooter";
import ChromeLogo from "../components/ChromeLogo";

const LandingPage = () => {
  const [email, setEmail] = useState("");
  const [currentText, setCurrentText] = useState("");
  const [showAfter, setShowAfter] = useState(false);
  
  const weakText = "sorry to bother, just circling back on this.";
  const strongText = "checking in to move this forward.";

   // Add this NEW useEffect here for handling #promode URLs
  useEffect(() => {
    // Check if there's a hash in the URL when the page loads
    if (window.location.hash) {
      const id = window.location.hash.substring(1); // Remove the '#'
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100); // Small delay to ensure page is fully loaded
    }
  }, []);
  
  // Auto-typing effect
  useEffect(() => {
    let timeoutId;
    
    const typeText = () => {
      let i = 0;
      const typing = setInterval(() => {
        if (i <= weakText.length) {
          setCurrentText(weakText.slice(0, i));
          i++;
        } else {
          clearInterval(typing);
          // Strike through and replace after delay
          timeoutId = setTimeout(() => {
            setShowAfter(true);
          }, 1000);
        }
      }, 100);
    };

    typeText();
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const handleWaitlistSubmit = async (e) => {
  e.preventDefault();
  
  if (email) {
    try {
      const url = `https://script.google.com/macros/s/AKfycbwj76bOdfZv6-eeHB66_c4AT4qvdHFSirldFm3_ci2G4voGBO8ZFuubH_FuOnn1CqCT1A/exec?email=${encodeURIComponent(email)}`;
      
      await fetch(url, {
        method: 'GET',
        mode: 'no-cors'
      });
      
      toast.success("You're on the waitlist! We'll email you when Pro Mode drops.");
      setEmail("");
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong. Please try again.');
    }
  }
};

  const handleInstallClick = () => {
    window.open("https://chromewebstore.google.com/detail/like-a-boss-%E2%80%94-confident-w/boamgnkbclegcgdohpoejbmkdgedoeek", "_blank"); //app store listing
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-white flex items-center justify-center px-4 py-20">
        <div className="container mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-black uppercase leading-tight text-black">
                STOP WRITING EMAILS LIKE An INTERN.
              </h1>
              <h2 className="text-2xl md:text-3xl font-bold text-black">
                Write emails. <span className="text-yellow-400">Like a Boss.</span>
              </h2>
            </div>
            
            {/* Auto-typing demo */}
            <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
              <div className="space-y-3">
                <div className="text-sm text-gray-600 font-medium">Email Draft:</div>
                <div className="font-mono text-lg">
                  {!showAfter ? (
                    <span className={showAfter ? "line-through text-red-500" : "text-gray-800"}>
                      {currentText}
                      <span className="animate-pulse">|</span>
                    </span>
                  ) : (
                    <div className="space-y-2">
                      <div className="line-through text-red-500 opacity-50">{weakText}</div>
                      <div className="text-green-600 font-bold animate-fade-in">{strongText}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <Button 
                onClick={handleInstallClick}
                className="bg-yellow-400 hover:bg-yellow-500 text-black text-xl font-bold py-6 px-8 rounded-full transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-3"
              >
                Install Like a Boss
                <ChromeLogo size={32} />
              </Button>
              <p className="text-sm text-gray-600 flex items-center gap-1">
  Free. Fast. For 
  <img src="https://likeaboss.biz/Gmail.svg" alt="Gmail" width="24" height="24" />
</p>
            </div>
          </div>

          <div className="relative">
            <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300">
              <img 
                src="https://likeaboss.biz/LikeABossScreenshot1.png"
                alt="Gmail Interface"
                className="w-full h-auto"
              />
              <div className="absolute top-4 right-4 bg-yellow-400 text-black px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                <Crown className="inline w-4 h-4 mr-1" />
                Boss Mode ON
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-black mb-4">
              It's like Grammarly... for people who actually want respect.
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow duration-300 border-2 hover:border-yellow-400">
              <CardContent className="p-8">
                <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-bold text-black mb-4">Stop Sounding Soft</h3>
                <p className="text-gray-600">We see the fluff before you hit send</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow duration-300 border-2 hover:border-yellow-400">
              <CardContent className="p-8">
                <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Edit3 className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-bold text-black mb-4">Rewrite With Authority</h3>
                <p className="text-gray-600">Swap hesitation for conviction</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow duration-300 border-2 hover:border-yellow-400">
              <CardContent className="p-8">
                <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Rocket className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-bold text-black mb-4">Own the Inbox</h3>
                <p className="text-gray-600">Send messages that get answers, ASAP</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Big Meme Moment Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-black mb-8">
              You think you sound polite. You actually sound like this:
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <X className="w-6 h-6 text-red-500" />
                    <Badge variant="destructive">Before</Badge>
                  </div>
                  <p className="text-lg font-mono text-gray-800">
                    "Just checking in on those TPS reports?"
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <Badge className="bg-green-500">After</Badge>
                  </div>
                  <p className="text-lg font-mono text-gray-800">
                    "Are you going to have those TPS reports for us this afternoon?"
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="relative">
              <div className="relative transform hover:scale-105 transition-transform duration-300">
                <p className="text-center text-black font-bold text-xl mb-4">
                  Confidence Level: <span className="underline decoration-yellow-400 decoration-4">BOSS</span>
                </p>
                <img 
                  src="https://likeaboss.biz/LikeABoss_LayoutImg2.jpeg"
                  alt="Boss Level Confidence"
                  className="w-48 h-48 mx-auto object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-black">
              Finally, an extension that gets it.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <Card className="text-center hover:bg-yellow-50 hover:shadow-lg transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">💪🏻</span>
                </div>
                <h3 className="font-extrabold text-lg text-black mb-2"> Confidence Upgrade</h3>
                <p className="text-gray-600 font-normal text-sm leading-relaxed">
                  Stronger writing, instantly
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:bg-yellow-50 hover:shadow-lg transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">🔒</span>
                </div>
                <h3 className="font-extrabold text-lg text-black mb-2">Privacy First</h3>
                <p className="text-gray-600 font-normal text-sm leading-relaxed">
                  Data never leaves your device (Sorry, Zuck)
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:bg-yellow-50 hover:shadow-lg transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">🧠</span>
                </div>
                <h3 className="font-extrabold text-lg text-black mb-2">Context Aware</h3>
                <p className="text-gray-600 font-normal text-sm leading-relaxed">
                  Knows the difference between "Sorry your dog died" vs. "Sorry to bother"
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:bg-yellow-50 hover:shadow-lg transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">💸</span>
                </div>
                <h3 className="font-extrabold text-lg text-black mb-2">Free (for now)</h3>
                <p className="text-gray-600 font-normal text-sm leading-relaxed">
                  No card, no catch
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Free vs Premium Section */}
      <section id="promode" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-black">
              Two ways to boss up.
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="bg-yellow-400 border-4 border-yellow-500 transform hover:scale-105 transition-transform duration-300">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-black text-black mb-6">FREE</h3>
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-black font-medium">'Almost Artificial' Intelligence </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-black font-medium">Full Gmail Support</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-black font-medium">100% free, no excuses</span>
                  </div>
                </div>
                <Button 
                  onClick={handleInstallClick}
                  className="bg-black hover:bg-gray-800 text-white font-bold py-3 px-6 rounded-full w-full flex items-center justify-center gap-3"
                >
                  Install Free
                  <ChromeLogo size={24} />
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-black border-4 border-gray-800 transform hover:scale-105 transition-transform duration-300">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-black text-yellow-400 mb-6">PRO MODE</h3>
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full"></div>
                    <span className="text-yellow-400 font-medium">Super Intelligent (Local) AI</span>
                  </div>
                  <div className="flex items-center gap-3">
  <div className="w-5 h-5 bg-yellow-400 rounded-full"></div>
  <span className="text-yellow-400 font-medium flex items-center gap-2">
    Supports 
    <img src="https://likeaboss.biz/Gmail.svg" alt="Gmail" width="24" height="24" />
    <img src="https://likeaboss.biz/Outlook.svg" alt="Outlook" width="24" height="24" />
    <img src="https://likeaboss.biz/slack.svg" alt="Slack" width="24" height="24" />
    <img src="https://likeaboss.biz/linkedin.svg" alt="LinkedIn" width="24" height="24" />
  </span>
</div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full"></div>
                    <span className="text-yellow-400 font-medium">Join the waitlist to be first in line</span>
                  </div>
                </div>
                <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                  />
                  <Button 
                    type="submit"
                    className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3 px-6 rounded-full w-full"
                  >
                    Unlocks Soon - Join the Waitlist
                  </Button>
                </form>
                <p className="text-sm text-gray-400 mt-4">
                  We'll email you when Pro Mode drops.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* CTA Footer Section */}
      <CTAFooter onInstallClick={handleInstallClick} />
    </div>
  );
};

export default LandingPage;
